/**
 * Ficha de un producto.
 *
 * Tres columnas: imagen y datos de identificación a la izquierda, descripción y
 * ficha técnica en el centro, y la columna de compra —precio, cantidad y
 * botones— fija a la derecha. Es la disposición de la v1 y se conserva porque
 * resuelve lo que hay que resolver: la decisión de comprar se toma con el precio
 * a la vista mientras se lee la ficha, sin tener que volver a subir.
 *
 * Lo que cambió respecto de la v1: el producto se pide al servidor
 * (`GET /api/catalogo/:id`), no se busca en un catálogo cargado en memoria. Un
 * producto inactivo o de un proveedor no aprobado responde 404 —igual que uno
 * inexistente—, así que escribir la URL a mano no es una puerta trasera.
 */
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useProducto } from '../api/hooks'
import { useSesion } from '../auth/sesion'
import { agregar, useCarrito } from '../carrito/estado'
import { DocumentacionArticulo } from '../componentes/DocumentacionArticulo'
import { ProductoTile } from '../componentes/ProductoTile'
import { Aviso, Dato, Money, Panel, Vacio } from '../componentes/ui'
import { IconoCarrito, IconoPin } from '../componentes/icons'
import { kg, num } from '../lib/format'

export function ProductoDetalle() {
  const { id = '' } = useParams()
  const { yo, puedeComprar, esOperativo } = useSesion()
  const navigate = useNavigate()
  const carrito = useCarrito()
  const [cantidad, setCantidad] = useState(1)

  const idBuscado = decodeURIComponent(id)
  const faenaId = yo ? carrito.faenaId : null
  const consulta = useProducto(idBuscado, faenaId)

  if (consulta.isPending) {
    return <p className="text-[13px] text-acero-500 py-8">Cargando…</p>
  }

  if (consulta.isError || !consulta.data) {
    return (
      <Vacio
        titulo="No encontramos ese producto"
        detalle="Puede que el proveedor lo haya dado de baja, que aún no esté homologado o que el enlace esté incompleto."
        accion={
          <Link to="/catalogo" className="btn btn-secundario">
            Volver al catálogo
          </Link>
        }
      />
    )
  }

  const producto = consulta.data
  const tope = producto.stockDisponible ?? 9_999

  const ficha = {
    nombre: producto.nombre,
    sku: producto.codigo,
    marca: producto.marca,
    categoriaId: producto.categoriaId,
    ...(producto.imagenUrl ? { imagenUrl: producto.imagenUrl } : {}),
  }

  function alAgregar(irAlCarrito: boolean) {
    /*
     * El artículo se guarda ANTES de cualquier redirección, y ese orden es todo
     * el arreglo. Antes se hacía al revés —`return navigate(...)` delante del
     * `agregar`— así que a quien no había entrado se lo mandaba a iniciar
     * sesión y su elección se perdía en el camino: entraba, caía en el carrito
     * y estaba vacío. Tenía que acordarse de qué había elegido y volver a
     * buscarlo, que es exactamente donde se abandona una compra.
     *
     * Guardar sin sesión no cuesta nada ni abre nada: el carrito vive en
     * `localStorage` y solo lleva el id del artículo y la cantidad. Mirar no
     * exige identidad y elegir tampoco; la identidad se exige al COTIZAR y al
     * EMITIR, que son los dos endpoints donde el servidor decide precios y
     * reserva stock.
     */
    agregar(producto.id, cantidad)
    // `/carrito` está detrás de `Privada`, así que después de entrar la propia
    // ruta lo lleva a su carrito, ya con esto dentro.
    if (!yo) return navigate('/entrar', { state: { volverA: '/carrito' } })
    if (irAlCarrito) navigate('/carrito')
  }

  return (
    <div className="space-y-3">
      <nav className="text-[12px] text-acero-500 flex gap-1.5">
        <Link to="/catalogo" className="hover:text-marca-600">
          Catálogo
        </Link>
        <span>/</span>
        {/* Texto y no enlace: los filtros del catálogo son estado de la
            pantalla, no de la URL, así que un enlace con `?categoria=` llevaría
            al catálogo sin filtrar y prometería algo que no ocurre. */}
        <span className="text-acero-700">{producto.categoriaNombre}</span>
      </nav>

      <div className="grid lg:grid-cols-[300px_1fr_290px] gap-3 items-start">
        <div className="panel overflow-hidden">
          <ProductoTile producto={ficha} alto="h-56" />
          <div className="p-3 text-[11.5px] text-acero-600 space-y-1">
            {/*
              CÓDIGO NUESTRO, no el del proveedor. El SKU identificaba la oferta
              y cambiaría el día que la oferta elegida cambie: el cliente habría
              guardado en su sistema un código que mañana significa otra cosa.
            */}
            <div className="flex justify-between gap-2">
              <span>Código</span>
              <span className="num text-acero-900">{producto.codigo}</span>
            </div>
            {/* El número de parte del fabricante sí es del fabricante: se publica. */}
            {producto.mpn && (
              <div className="flex justify-between gap-2">
                <span>N.º de parte</span>
                <span className="num text-acero-900">{producto.mpn}</span>
              </div>
            )}
            <div className="flex justify-between gap-2">
              <span>Marca</span>
              <span className="text-acero-900">{producto.marca}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span>Categoría</span>
              <span className="text-acero-900">{producto.categoriaNombre}</span>
            </div>
          </div>
        </div>

        <div className="space-y-3 min-w-0">
          <div className="panel p-4">
            <h1 className="text-[20px] leading-tight">{producto.nombre}</h1>
            <p className="mt-2 text-[13px] text-acero-700 leading-relaxed">{producto.descripcion}</p>

            <div className="mt-3 flex items-center gap-2 flex-wrap text-[12.5px]">
              {/*
                "Vendido por Compranorte" es literal: se le compra a nosotros y
                nosotros le compramos al distribuidor. La identidad del
                distribuidor no aparece acá ni con sesión.
              */}
              <span className="text-acero-600">Vendido por Compranorte · despacha desde</span>
              <strong>{producto.ciudadOrigen}</strong>
              {/* Cuántas ofertas hay detrás es dato del equipo. */}
              {producto.ofertas !== undefined && (
                <span className="chip">
                  {producto.ofertas === 1 ? '1 oferta' : `${producto.ofertas} ofertas`}
                </span>
              )}
            </div>
          </div>

          <Panel titulo="Ficha técnica y datos de despacho" padding={false}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4 p-3">
              <Dato etiqueta="Peso real" valor={<span className="num">{kg(producto.pesoKg)}</span>} />
              <Dato
                etiqueta="Dimensiones (L×A×A)"
                valor={
                  <span className="num">
                    {num(producto.largoCm)} × {num(producto.anchoCm)} × {num(producto.altoCm)} cm
                  </span>
                }
              />
              <Dato
                etiqueta="Stock"
                valor={
                  <span className="num">
                    {/* El stock EXACTO es información comercial: sin sesión solo
                        viaja si hay o no. */}
                    {producto.stockDisponible !== undefined
                      ? `${num(producto.stockDisponible)} un.`
                      : producto.hayStock
                        ? 'Disponible'
                        : 'Agotado'}
                  </span>
                }
              />
              <Dato
                etiqueta="Plazo estimado a faena"
                valor={
                  <span className="num">
                    {yo
                      ? `${num(producto.diasEntrega)} días hábiles`
                      : 'Inicie sesión para verlo'}
                  </span>
                }
              />
            </div>

            {!yo && (
              <div className="px-3 pb-3">
                <Aviso tono="info">
                  El plazo de entrega y el stock exacto están disponibles para clientes
                  registrados: dependen de la zona de su faena y de las tarifas, que no son
                  públicas.
                </Aviso>
              </div>
            )}

            <div className="px-3 pb-3">
              <Aviso tono="info">
                El flete se cobra por <strong>peso real</strong>, y se calcula sobre el carrito
                completo y no por producto: si suma varios artículos del mismo origen, el flete
                unitario baja.
              </Aviso>
            </div>
          </Panel>

          {/*
            La documentación va DESPUÉS de la ficha técnica y antes de nada más.
            Es el orden en que se lee: primero las cifras que deciden el flete y
            el plazo, después el PDF que confirma que es el repuesto correcto.

            El componente se dibuja solo si hay documentos, así que un artículo
            sin nada colgado no deja un panel vacío prometiendo lo que no está.
          */}
          <DocumentacionArticulo articuloId={producto.id} />
        </div>

        <div className="panel p-3.5 lg:sticky lg:top-12">
          <div className="text-[26px] font-semibold leading-none">
            <Money v={producto.precioVentaNeto} />
          </div>
          <div className="text-[11.5px] text-acero-500 mt-1">
            neto por unidad · el IVA se agrega en el resumen del carrito
          </div>

          <div className="mt-3">
            <label className="etiqueta mb-0 flex items-center gap-2">
              Cantidad
              <input
                type="number"
                min={1}
                max={tope}
                value={cantidad}
                onChange={(e) =>
                  setCantidad(Math.min(tope, Math.max(1, Math.floor(Number(e.target.value) || 1))))
                }
                className="campo num w-20 text-right"
              />
            </label>
            {producto.stockDisponible !== undefined && cantidad >= producto.stockDisponible && (
              <p className="ayuda">Es todo el stock disponible de este producto.</p>
            )}
          </div>

          <dl className="mt-3 border-t border-acero-200 pt-2 text-[12.5px] space-y-1">
            <div className="flex justify-between gap-2">
              <dt className="text-acero-600">Subtotal neto</dt>
              <dd className="font-semibold">
                <Money v={producto.precioVentaNeto * cantidad} />
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-acero-600">Peso real del bulto</dt>
              <dd className="num">{kg(producto.pesoKg * cantidad)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-acero-600">Origen</dt>
              <dd>{producto.ciudadOrigen}</dd>
            </div>
          </dl>

          <div className="mt-3 grid gap-2">
            {/*
              Un proveedor, una cuenta sin rol y una de SOLO LECTURA no compran:
              en vez de un botón que responde con un error, no hay botón. Quien
              no entró sí lo ve, porque para él es una invitación a entrar.
            */}
            {((puedeComprar && esOperativo) || !yo) && (
              <>
                <button
                  type="button"
                  className="btn btn-accion w-full"
                  onClick={() => alAgregar(false)}
                  disabled={!producto.hayStock}
                >
                  {/*
                    El rótulo nombra las DOS cosas que pasan al pulsarlo. Decir
                    solo "Entrar para comprar" escondía que la elección se
                    guarda, y decir solo "Agregar al carrito" convertiría el
                    salto al login en una sorpresa.
                  */}
                  <IconoCarrito size={15} /> {yo ? 'Agregar al carrito' : 'Agregar y entrar'}
                </button>
                {yo && (
                  <button
                    type="button"
                    className="btn btn-secundario w-full"
                    onClick={() => alAgregar(true)}
                    disabled={!producto.hayStock}
                  >
                    Agregar e ir al carrito
                  </button>
                )}
              </>
            )}
            {/*
              A un lector se le dice POR QUÉ no hay botón, y a quién pedírselo.
              Sin esta línea la columna de compra queda muda y parece rota.
            */}
            {yo && puedeComprar && !esOperativo && (
              <p className="ayuda text-center">
                Su cuenta es de solo lectura. Pida acceso operativo al administrador de su empresa
                para comprar.
              </p>
            )}
            {!producto.hayStock && (
              <p className="ayuda text-center">
                Sin stock por ahora. Escríbanos si lo necesita: lo reponemos con el proveedor.
              </p>
            )}
          </div>

          <p className="mt-3 text-[11.5px] text-acero-500 leading-relaxed flex gap-1.5">
            <IconoPin size={14} className="shrink-0 mt-0.5" />
            El flete y el plazo definitivos se calculan al elegir la faena de destino en el carrito.
          </p>
        </div>
      </div>
    </div>
  )
}
